import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import ScaleToFit from "./ScaleToFit";
import SideStarField from "./SideStarField";
import { useIsPortrait } from "./useIsPortrait";
import HTLDSlide6bMobile from "./HTLDSlide6bMobile";
import { useVisibleAnimation } from "./useVisibleAnimation";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { pick } from "i18n/lang";
import { slide6bContent, useSlide6b, Seg } from "./slide6b.data";

// Single accent, used only to highlight the Right-nostril / REM moments.
// Everything else in the graphic is white/gray/black — no second hue to compete with it.
const accent = "#c76a94";   // dusty rose
const font   = FONT_BODY;

const S: Record<string, CSSProperties> = {
root: {
background: "#040f18", fontFamily: font, color: "#d0e8f8",
width: "100%", height: "100%",
display: "flex", flexDirection: "column",
boxSizing: "border-box",
overflow: "hidden",
},
subtitleBar: {
background: "#050f1a", borderBottom: "1px solid rgba(199,106,148,0.1)",
padding: "clamp(5px,0.6vw,9px) clamp(16px,3vw,40px)",
display: "flex", alignItems: "center", justifyContent: "space-between",
gap: "clamp(12px,2vw,28px)", flexShrink: 0,
},
body: {
flex: 1,
minHeight: 0,
display: "flex", gap: "clamp(16px,2vw,28px)",
padding: "clamp(10px,1.4vw,18px) clamp(16px,3vw,40px) clamp(10px,1.2vw,14px)",
alignItems: "stretch",
overflow: "hidden",
},
content: {
flex: 1, minWidth: "min(100%, 400px)",
display: "flex", flexDirection: "column",
justifyContent: "space-between",
},
panel: {
width: "clamp(220px,28vw,400px)", flexShrink: 0,
display: "flex", flexDirection: "column",
},
};

// Same sleep architecture data as the Sleep Cycle slide, so the two slides
// stay factually consistent.
const sleepData: { t: number; s: number }[] = [
{t:0.00,s:0},{t:0.18,s:2},{t:0.38,s:3},{t:0.95,s:3},{t:1.10,s:2},{t:1.30,s:1},
{t:1.65,s:2},{t:1.85,s:3},{t:2.30,s:3},{t:2.50,s:2},{t:2.70,s:1},{t:3.15,s:2},
{t:3.30,s:3},{t:3.65,s:3},{t:3.85,s:2},{t:4.05,s:1},{t:4.70,s:2},{t:4.88,s:3},
{t:5.10,s:2},{t:5.28,s:1},{t:6.08,s:2},{t:6.25,s:1},{t:7.10,s:2},{t:7.30,s:1},
{t:7.90,s:2},{t:8.00,s:0},
];

// ─── Sleep Graph + Nostril Indicator Canvas ──────────────────────────────────
const SleepNostrilGraph: React.FC = () => {
const canvasRef = useRef<HTMLCanvasElement>(null);
const drawFnRef = useRef<null | (() => void)>(null);
const dtRef     = useRef<number>(0);
useEffect(() => {
  const canvas = canvasRef.current; if (!canvas) return;
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D; if (!ctx) return;
  const W = 460, H = 560;
  const c = pick(slide6bContent).canvas; // slides remount on language change

  const dpr = window.devicePixelRatio || 1;
  canvas.width  = W * dpr;
  canvas.height = H * dpr;
  ctx.scale(dpr, dpr);

  const GX = 62, GY = 50, GW = 368, GH = 150, TOTAL_H = 8;
  const stageY: Record<number, number> = { 0:GY, 1:GY+GH*0.28, 2:GY+GH*0.58, 3:GY+GH };
  const stageLabels = c.stageLabels;
  const tx = (t: number) => GX + (t / TOTAL_H) * GW;

  const pts: { x: number; y: number }[] = [];
  sleepData.forEach((d, i) => {
    const x = tx(d.t), y = stageY[d.s];
    if (i > 0) pts.push({ x, y: stageY[sleepData[i-1].s] });
    pts.push({ x, y });
  });

  // REM windows drive both the REM band on the graph AND the right-nostril
  // indicator below.
  const remWindows: { start: number; end: number }[] = [];
  { let inRem=false, start=0;
    sleepData.forEach(d => {
      if (d.s===1 && !inRem) { inRem=true; start=d.t; }
      if (d.s!==1 && inRem)  { inRem=false; remWindows.push({start, end:d.t}); }
    });
    if (inRem) remWindows.push({ start, end: sleepData[sleepData.length-1].t });
  }
  const isREM = (t: number) => remWindows.some(w => t >= w.start && t < w.end);

  const ANIM_FRAMES = 750;
  let frame = 0;

  const starSeeds = [[25,20,1.0],[88,10,0.8],[350,16,1.1],[420,40,0.7],[45,66,0.9],[390,62,1.0],[445,12,1.0],[130,44,0.8]];
  const stars = [
    ...starSeeds.map(([x,y,r]) => ({ x, y, r, tw: Math.random()*Math.PI*2, spd: 0.010+Math.random()*0.022 })),
    ...Array.from({ length: 26 }, () => ({
      x: Math.random()*W, y: Math.random()*(GY-8)+4,
      r: 0.3+Math.random()*0.9,
      tw: Math.random()*Math.PI*2, spd: 0.010+Math.random()*0.022,
    })),
  ];

  // Nostril circle, styled after the Nadi Shuddhi slide's breath-guide nostrils.
  // colorRgb is the nostril's OWN colour family (gold for Left, green for
  // Right) — open = bright, closed = the same hue dimmed, so Left never
  // borrows Right's colour.
  function drawNostril(x: number, y: number, label: string, isOpen: boolean, colorRgb: string) {
    const r = 28;
    const col = `rgb(${colorRgb})`;
    if (isOpen) {
      const grd = ctx.createRadialGradient(x, y, 0, x, y, r + 16);
      grd.addColorStop(0, `rgba(${colorRgb},0.30)`); grd.addColorStop(1, `rgba(${colorRgb},0)`);
      ctx.beginPath(); ctx.arc(x, y, r + 16, 0, Math.PI*2); ctx.fillStyle = grd; ctx.fill();
    }
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI*2);
    ctx.fillStyle   = isOpen ? `rgba(${colorRgb},0.20)` : `rgba(${colorRgb},0.06)`; ctx.fill();
    ctx.strokeStyle = isOpen ? `rgba(${colorRgb},0.85)` : `rgba(${colorRgb},0.28)`;
    ctx.lineWidth   = isOpen ? 2.2 : 1.0; ctx.stroke();
    if (!isOpen) {
      ctx.strokeStyle = `rgba(${colorRgb},0.35)`; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(x-8,y-8); ctx.lineTo(x+8,y+8);
      ctx.moveTo(x+8,y-8); ctx.lineTo(x-8,y+8); ctx.stroke();
    } else {
      ctx.beginPath(); ctx.arc(x, y, 5, 0, Math.PI*2); ctx.fillStyle = col; ctx.fill();
    }
    ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
    ctx.font = "bold 13px 'JetBrains Mono','Courier New',monospace";
    ctx.fillStyle = isOpen ? col : `rgba(${colorRgb},0.45)`;
    ctx.fillText(label, x, y + r + 17);
    ctx.font = "bold 10px 'JetBrains Mono','Courier New',monospace";
    ctx.fillStyle = isOpen ? `rgba(${colorRgb},0.95)` : `rgba(${colorRgb},0.38)`;
    ctx.fillText(isOpen ? c.open : c.closed, x, y + r + 30);
  }

  function draw() {
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#040f18"; ctx.fillRect(0, 0, W, H);

    const pulse = 0.5 + 0.5 * Math.sin(frame * 0.14);

    // ── Stars ────────────────────────────────────────────────────────────
    stars.forEach(s => {
      const tw = 0.25 + 0.75 * Math.abs(Math.sin(frame * s.spd + s.tw));
      ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI*2);
      ctx.fillStyle = `rgba(255,255,255,${(tw*0.75).toFixed(2)})`; ctx.fill();
    });

    // ── Moon ─────────────────────────────────────────────────────────────
    [[16,0.07],[10,0.13],[6,1]].forEach(([r,a],i) => {
      ctx.beginPath(); ctx.arc(415,32,r,0,Math.PI*2);
      ctx.fillStyle = i<2 ? `rgba(255,252,180,${a})` : "#fffde7"; ctx.fill();
    });

    // ── Graph title ─────────────────────────────────────────────────────
    ctx.font="bold 14px 'JetBrains Mono','Courier New',monospace";
    ctx.fillStyle="#e8fbfb";
    ctx.textAlign="center"; ctx.fillText(c.title,GX+GW/2,GY-14);

    // ── Graph background + grid ─────────────────────────────────────────
    ctx.fillStyle="rgba(199,106,148,0.04)"; ctx.fillRect(GX,GY,GW,GH);
    [0,1,2,3].forEach(s => {
      ctx.beginPath(); ctx.moveTo(GX,stageY[s]); ctx.lineTo(GX+GW,stageY[s]);
      ctx.strokeStyle="rgba(199,106,148,0.12)"; ctx.lineWidth=1; ctx.setLineDash([4,6]); ctx.stroke(); ctx.setLineDash([]);
    });

    // ── Stage labels ─────────────────────────────────────────────────────
    stageLabels.forEach((label,i) => {
      ctx.font="bold 11px 'JetBrains Mono','Courier New',monospace";
      ctx.fillStyle = i===1 ? accent : "rgba(255,255,255,0.85)";
      ctx.textAlign="right"; ctx.fillText(label.toUpperCase(), GX-8, stageY[i]+3);
    });

    // ── Axes ─────────────────────────────────────────────────────────────
    ctx.beginPath(); ctx.moveTo(GX,GY-6); ctx.lineTo(GX,GY+GH+8); ctx.moveTo(GX,GY+GH+8); ctx.lineTo(GX+GW,GY+GH+8);
    ctx.strokeStyle="rgba(199,106,148,0.35)"; ctx.lineWidth=1; ctx.stroke();

    // ── REM bands ────────────────────────────────────────────────────────
    remWindows.forEach(w => {
      ctx.fillStyle="rgba(199,106,148,0.16)"; ctx.fillRect(tx(w.start),GY,tx(w.end)-tx(w.start),GH);
    });

    // ── Area fill under sleep line ─────────────────────────────────────────
    ctx.save();
    ctx.beginPath();
    pts.forEach((p,i) => i===0 ? ctx.moveTo(p.x,p.y) : ctx.lineTo(p.x,p.y));
    ctx.lineTo(pts[pts.length-1].x, GY+GH); ctx.lineTo(pts[0].x, GY+GH); ctx.closePath();
    const aGrd = ctx.createLinearGradient(0, GY, 0, GY+GH);
    aGrd.addColorStop(0,    "rgba(199,106,148,0.26)");
    aGrd.addColorStop(0.55, "rgba(199,106,148,0.10)");
    aGrd.addColorStop(1,    "rgba(199,106,148,0.02)");
    ctx.fillStyle = aGrd; ctx.fill();
    ctx.restore();

    // ── Sleep line ───────────────────────────────────────────────────────
    ctx.beginPath();
    pts.forEach((p,i) => i===0 ? ctx.moveTo(p.x,p.y) : ctx.lineTo(p.x,p.y));
    ctx.strokeStyle="rgba(199,106,148,0.18)"; ctx.lineWidth=9; ctx.lineJoin="round"; ctx.stroke();
    ctx.strokeStyle="rgba(199,106,148,0.32)"; ctx.lineWidth=4; ctx.lineJoin="round"; ctx.stroke();
    ctx.beginPath();
    pts.forEach((p,i) => i===0 ? ctx.moveTo(p.x,p.y) : ctx.lineTo(p.x,p.y));
    ctx.strokeStyle=accent; ctx.lineWidth=2; ctx.lineJoin="round"; ctx.stroke();

    // ── Where "now" is, tonight ──────────────────────────────────────────
    const prog = (frame % ANIM_FRAMES) / ANIM_FRAMES;
    const curT = prog * TOTAL_H;
    const dotRight = isREM(curT);
    const activeRgb = dotRight ? "199,106,148" : "255,255,255";
    const px = tx(curT);

    // ── Time ticks ───────────────────────────────────────────────────────
    for (let h = 0; h <= TOTAL_H; h++) {
      const x = tx(h);
      ctx.beginPath(); ctx.moveTo(x,GY+GH); ctx.lineTo(x,GY+GH+5);
      ctx.strokeStyle="rgba(199,106,148,0.40)"; ctx.lineWidth=1; ctx.stroke();
      ctx.font="11px 'JetBrains Mono','Courier New',monospace";
      ctx.fillStyle="rgba(160,225,225,0.95)";
      ctx.textAlign="center"; ctx.fillText(`${h}h`,x,GY+GH+16);
    }

    // ── Cycle markers ───────────────────────────────────────────────────
    [{start:0,end:1.65,label:`${c.cycle} 1`},{start:1.65,end:3.30,label:`${c.cycle} 2`},{start:3.30,end:5.28,label:`${c.cycle} 3`},{start:5.28,end:7.10,label:`${c.cycle} 4`}].forEach(cy => {
      const x1=tx(cy.start)+2, x2=tx(cy.end)-2, by=GY+GH+30;
      ctx.beginPath(); ctx.moveTo(x1,by); ctx.lineTo(x1,by+4); ctx.lineTo(x2,by+4); ctx.lineTo(x2,by);
      ctx.strokeStyle="rgba(199,106,148,0.45)"; ctx.lineWidth=1; ctx.stroke();
      ctx.font="10px 'JetBrains Mono','Courier New',monospace";
      ctx.fillStyle="rgba(170,230,230,0.92)";
      ctx.textAlign="center"; ctx.fillText(cy.label,(x1+x2)/2,by+14);
    });

    // ══════════════════════════════════════════════════════════════════════
    // SWARA · NOSTRIL DOMINANCE STRIP — same x-axis as the graph above
    // ══════════════════════════════════════════════════════════════════════
    const STRIP_Y = GY + GH + 64, STRIP_H = 24;
    ctx.font="bold 11px 'JetBrains Mono','Courier New',monospace";
    ctx.fillStyle="rgba(190,235,235,0.95)";
    ctx.textAlign="left"; ctx.fillText(c.stripTitle, GX, STRIP_Y-7);

    ctx.fillStyle="rgba(199,106,148,0.10)"; ctx.fillRect(GX,STRIP_Y,GW,STRIP_H);
    const SAMPLES = 240;
    for (let i = 0; i < SAMPLES; i++) {
      const t0 = (i/SAMPLES)*TOTAL_H, t1 = ((i+1)/SAMPLES)*TOTAL_H;
      const right = isREM((t0+t1)/2);
      ctx.fillStyle = right ? "rgba(199,106,148,0.85)" : "rgba(255,255,255,0.30)";
      ctx.fillRect(tx(t0), STRIP_Y, tx(t1)-tx(t0)+0.5, STRIP_H);
    }
    // L / R segment labels
    let segStart = 0, segRight = isREM(0);
    const segs: { start: number; end: number; right: boolean }[] = [];
    for (let i = 1; i <= SAMPLES; i++) {
      const t = (i/SAMPLES)*TOTAL_H;
      const right = i < SAMPLES ? isREM(t) : !segRight;
      if (right !== segRight || i === SAMPLES) {
        segs.push({ start: segStart, end: t, right: segRight });
        segStart = t; segRight = right;
      }
    }
    segs.forEach(seg => {
      const w = tx(seg.end) - tx(seg.start);
      if (w < 16) return;
      ctx.font="bold 12px 'JetBrains Mono','Courier New',monospace";
      ctx.fillStyle = "#ffffff";
      ctx.shadowColor = "rgba(0,0,0,0.55)"; ctx.shadowBlur = 3;
      ctx.textAlign="center"; ctx.textBaseline="middle";
      ctx.fillText(seg.right ? c.stripRight : c.stripLeft, (tx(seg.start)+tx(seg.end))/2, STRIP_Y+STRIP_H/2+0.5);
      ctx.shadowBlur = 0;
    });
    ctx.textBaseline="alphabetic";
    ctx.strokeStyle="rgba(199,106,148,0.40)"; ctx.lineWidth=1; ctx.strokeRect(GX,STRIP_Y,GW,STRIP_H);

    // ── Shared playhead line — overlaps the graph AND the strip below ────
    ctx.beginPath(); ctx.moveTo(px, GY-4); ctx.lineTo(px, STRIP_Y+STRIP_H+4);
    ctx.strokeStyle=`rgba(${activeRgb},0.80)`; ctx.lineWidth=2; ctx.stroke();
    [GY-4, STRIP_Y+STRIP_H+4].forEach(py => {
      const pg = ctx.createRadialGradient(px,py,0,px,py,10+pulse*3);
      pg.addColorStop(0, `rgba(${activeRgb},${(0.6*pulse).toFixed(3)})`); pg.addColorStop(1,"rgba(199,106,148,0)");
      ctx.beginPath(); ctx.arc(px,py,10+pulse*3,0,Math.PI*2); ctx.fillStyle=pg; ctx.fill();
      ctx.beginPath(); ctx.arc(px,py,2.8,0,Math.PI*2); ctx.fillStyle="#ffffff"; ctx.fill();
    });

    // ══════════════════════════════════════════════════════════════════════
    // NOSTRIL INDICATOR — same L/R breath-guide language as Nadi Shuddhi
    // ══════════════════════════════════════════════════════════════════════
    const NY = STRIP_Y + STRIP_H + 28;
    ctx.font="bold 11px 'JetBrains Mono','Courier New',monospace";
    ctx.fillStyle="rgba(190,235,235,0.95)";
    ctx.textAlign="center"; ctx.fillText(c.nowTitle, GX+GW/2, NY);

    const cyN = NY + 42;
    drawNostril(GX+GW/2-55, cyN, c.left,  !dotRight, "255,255,255");
    drawNostril(GX+GW/2+55, cyN, c.right,  dotRight, "199,106,148");

    // ══════════════════════════════════════════════════════════════════════
    // PHASE PAIRING
    // ══════════════════════════════════════════════════════════════════════
    const BY = cyN + 28 + 38;
    ctx.font="bold 13px 'JetBrains Mono','Courier New',monospace";
    ctx.fillStyle="#e8fbfb";
    ctx.textAlign="left"; ctx.fillText(c.pairingTitle, GX, BY);

    const pairColors = ["rgba(255,255,255,0.90)", accent, "rgba(255,255,255,0.85)"];
    const pairs = c.pairs.map((p, i) => ({ ...p, color: pairColors[i] }));

    pairs.forEach((p, i) => {
      const py = BY + 22 + i * 38;
      const isR = !!p.right;
      ctx.font = isR ? "bold 13px 'JetBrains Mono','Courier New',monospace" : "13px 'JetBrains Mono','Courier New',monospace";
      ctx.fillStyle = p.color; ctx.textAlign="left";
      if (isR) { ctx.shadowColor=accent; ctx.shadowBlur=6; }
      ctx.fillText(p.tag, GX, py+8);
      ctx.shadowBlur=0;

      ctx.font="12px 'Space Grotesk',Arial,sans-serif";
      ctx.fillStyle = isR ? "#ffffff" : "rgba(255,255,255,0.80)";
      ctx.textAlign="left";
      ctx.fillText(p.text, GX, py+26);
    });

frame += dtRef.current > 0 ? dtRef.current * 60 : 1;
  }

  drawFnRef.current = draw;
  return () => { drawFnRef.current = null; };
}, []);

useVisibleAnimation(canvasRef, (dt) => { dtRef.current = dt; drawFnRef.current?.(); }, { fps: 30 });

return (
  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
    <canvas
      ref={canvasRef}
      style={{ display: "block", borderRadius: 8, width: "100%", height: "100%", objectFit: "contain" }}
    />
  </div>
);
};

// ─── Slide content (authored at the ScaleToFit design size) ────────────────────
const HTLDSlide6bInner: React.FC = () => {
const { meta, pairings, rhythm, callout } = useSlide6b();
const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <span key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</span>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );
const fontLinkRef = useRef<HTMLLinkElement | null>(null);

useEffect(() => {
const existing = document.querySelector(
  'link[href*="Space+Grotesk"]'
) as HTMLLinkElement | null;

if (!existing) {
  const link = document.createElement("link");
  link.rel  = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
  document.head.appendChild(link);
  fontLinkRef.current = link;
}

return () => {
  if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
    document.head.removeChild(fontLinkRef.current);
    fontLinkRef.current = null;
  }
};
}, []);

const lbl: CSSProperties = {
fontSize: "clamp(8px,0.7vw,11px)", fontFamily: FONT_MONO,
color: accent, letterSpacing: 3, textTransform: "uppercase",
marginBottom: "clamp(4px,0.55vw,8px)", fontStyle: "normal",
};
const txt: CSSProperties = {
fontSize: "clamp(12px,1.0vw,15px)", fontStyle: "normal", fontFamily: FONT_BODY,
lineHeight: 1.7, color: "#cde0f2", margin: 0,
};

const rootRef = useRef<HTMLDivElement>(null)

return (
<div style={S.root} ref={rootRef}>
<SideStarField rootRef={rootRef} />

  {/* Header row: breadcrumb (left) + slide title (right), above the horizontal line */}
  <div style={S.subtitleBar}>
    <div style={{
      fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal", color: "#7aa8cc",
      fontFamily: FONT_BODY,
    }}>
      {meta.breadcrumbPre} &nbsp;·&nbsp;{" "}
      <span style={{ color: accent }}>{meta.breadcrumbAccent}</span>
    </div>
    <div style={{
      fontSize: "clamp(16px,1.9vw,28px)", fontStyle: "normal", fontWeight: 600,
      color: "#e8f4ff", fontFamily: FONT_DISPLAY, letterSpacing: 0.5, lineHeight: 1,
      whiteSpace: "nowrap",
    }}>
      {meta.headerTitlePre}<span style={{ color: accent }}>{meta.headerTitleAccent}</span>
    </div>
  </div>

  {/* Body */}
  <div style={S.body}>

    {/* Content */}
    <div style={S.content}>

      {/* Title block */}
      <div>
        <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(19px,2.3vw,30px)", fontStyle: "normal", fontWeight: 600, color: "#e8f4ff", margin: "0 0 clamp(2px,0.35vw,6px)", lineHeight: 1.2 }}>
          {meta.titlePre}<span style={{ color: accent }}>{meta.titleAccentWord}</span>{meta.titlePost}
        </h1>
        <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.1vw,15px)", fontStyle: "normal", fontWeight: 300, color: "#7aa8cc", margin: 0, letterSpacing: 0.3 }}>
          {meta.subtitle}
        </p>
      </div>

      {/* Shared rhythm */}
      <div style={{ borderBottom: "1px solid rgba(199,106,148,0.10)", paddingBottom: "clamp(8px,1vw,14px)" }}>
        <div style={lbl}>{meta.rhythmLabel}</div>
        <p style={txt}>{renderSegs(rhythm, "#f0c9dc")}</p>
      </div>

      {/* Pairing cards */}
      <div style={{ borderBottom: "1px solid rgba(199,106,148,0.10)", paddingBottom: "clamp(8px,1vw,14px)" }}>
        <div style={lbl}>{meta.pairLabel}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "clamp(5px,0.7vw,9px)" }}>
          {pairings.map((p, i) => (
            <div key={i} style={{ display: "flex", gap: "clamp(10px,1.2vw,16px)", alignItems: "flex-start", background: p.highlight ? "rgba(199,106,148,0.08)" : "rgba(255,255,255,0.02)", border: `1px solid ${p.highlight ? "rgba(199,106,148,0.30)" : "rgba(255,255,255,0.08)"}`, borderRadius: 7, padding: "clamp(7px,0.9vw,11px) clamp(10px,1.1vw,14px)" }}>
              <div style={{ flexShrink: 0, width: "clamp(64px,7vw,92px)" }}>
                <div style={{ fontSize: "clamp(8px,0.7vw,11px)", fontFamily: FONT_MONO, color: p.highlight ? accent : "rgba(255,255,255,0.55)", letterSpacing: 1 }}>{p.side.toUpperCase()}</div>
                <div style={{ fontSize: "clamp(8px,0.65vw,10px)", fontFamily: FONT_MONO, color: "#4a6880", marginTop: 2 }}>{p.nadi}</div>
              </div>
              <div>
                <div style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.0vw,15px)", fontStyle: "normal", fontWeight: 700, color: p.highlight ? "#f0c9dc" : "#c8dff0", marginBottom: 2 }}>{p.state}</div>
                <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(11px,0.92vw,14px)", fontStyle: "normal", fontWeight: 300, lineHeight: 1.55, color: p.highlight ? "#e3b4c9" : "#9bbdd6", margin: 0 }}>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Callout */}
      <div style={{ background: "rgba(199,106,148,0.06)", border: "1px solid rgba(199,106,148,0.20)", borderRadius: 8, padding: "clamp(11px,1.3vw,18px) clamp(13px,1.5vw,20px)" }}>
        <div style={lbl}>{meta.calloutLabel}</div>
        <p style={{ ...txt, color: "#e3b4c9" }}>{renderSegs(callout, "#f7d9e8")}</p>
      </div>

    </div>

    {/* Panel — sleep graph + nostril indicator fills the full panel height */}
    <div style={S.panel}>
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <SleepNostrilGraph />
      </div>
    </div>

  </div>

</div>
);
};

// Scale the whole slide as one unit so it looks identical across screen
// resolutions, window sizes, and browser zoom — no reflow, no clipping.
const HTLDSlide6b: React.FC = () => {
  const portrait = useIsPortrait();
  return portrait
    ? <HTLDSlide6bMobile />
    : <ScaleToFit><HTLDSlide6bInner /></ScaleToFit>;
};

export default HTLDSlide6b;
